/**
 * DynBox store (Faz 11.3 — OCS DynBox { label, value, active, locked } parity).
 *
 * Carries the EDITING state of the multi-field dynamic-input balloon: which
 * fields exist for the current step (X/Y coordinate pair + polar
 * Distance/Angle pair), which field the keyboard owns, typed values and
 * value locks. Live cursor-derived values are NOT stored — the balloon
 * provider derives them from the dynamic-input engine refs at render time
 * (pointer storms never write this store). Transient — nothing persists.
 *
 * The commit path reuses the classic entry grammar: typed values compose
 * "x,y" / "d<a" strings that ride resolveDynamicPoint + feedInteract /
 * injectPointAtWorld — the input injection pipeline is untouched.
 */

import { create } from 'zustand';

export type DynFieldLabel = 'X' | 'Y' | 'Z' | 'Distance' | 'Angle' | 'Scalar';

export interface DynField {
    label: DynFieldLabel;
    /** User-typed text; null = the field follows the live cursor value. */
    typed: string | null;
    /** Locked fields keep their value while the cursor moves. */
    locked: boolean;
}

/**
 * Balon modes:
 *  - 'xy'     first point of a gesture — the X/Y coordinate pair only.
 *  - 'point'  subsequent points (a base point exists) — the X/Y pair PLUS
 *             the polar Distance/Angle pair (AutoCAD DYNMODE 3 layout: Tab
 *             walks X → Distance → Angle, the classic coordinate→dimensional
 *             toggle; ',' reaches Y).
 *  - 'scalar' numeric steps — one Scalar field.
 */
export type DynBoxMode = 'xy' | 'point' | 'scalar';

interface DynBoxState {
    /** Current field set; null = the balloon shows no editable fields. */
    mode: DynBoxMode | null;
    fields: DynField[];
    /** Focused field index (keyboard entry target); null = cursor-live only. */
    activeIndex: number | null;
    /** Relative entry mode ('@' first char / '#' badge) for the X/Y pair. */
    relative: boolean;

    startEditing: (mode: DynBoxMode) => void;
    stopEditing: () => void;
    setActiveIndex: (index: number | null) => void;
    /** Tab: advance along the mode's focus order. */
    cycleField: () => void;
    /** ',': toggle focus inside the X/Y pair. */
    commaField: () => void;
    /** Type one character into the focused (or first) field. */
    typeChar: (ch: string) => void;
    backspace: () => void;
    /** Lock the focused field's typed value (Enter / pin click). */
    lockActive: () => void;
    setRelative: (value: boolean) => void;
}

const FIELDS_FOR: Record<DynBoxMode, DynField[]> = {
    xy: [
        { label: 'X', typed: null, locked: false },
        { label: 'Y', typed: null, locked: false },
    ],
    point: [
        { label: 'X', typed: null, locked: false },
        { label: 'Y', typed: null, locked: false },
        { label: 'Distance', typed: null, locked: false },
        { label: 'Angle', typed: null, locked: false },
    ],
    scalar: [
        { label: 'Scalar', typed: null, locked: false },
    ],
};

/**
 * Tab focus order per mode (Faz 11.3): X toggles onto the dimensional pair
 * (Distance → Angle) once it exists — Y stays cursor-live and is reached
 * with ',' (classic coordinate comma). Scalar wraps on itself.
 */
export function nextFieldIndex(mode: DynBoxMode, fields: DynField[], active: number | null): number {
    const count = fields.length;
    if (count === 0) return 0;
    if (mode === 'point') {
        // X(0) → Distance(2) → Angle(3) → X(0); Y(1) via ',' only.
        if (active === null || active === 3) return 0;
        if (active === 0) return 2;
        return 3;
    }
    if (mode === 'xy') {
        if (active === null || active === 1) return 0;
        return 1;
    }
    return 0;
}

export const useDynBoxStore = create<DynBoxState>((set, get) => ({
    mode: null,
    fields: [],
    activeIndex: null,
    relative: false,

    startEditing: (mode) => {
        // Always rebuild: the caller re-runs this on every step advance, and
        // a step change must hand the user a fresh field set.
        set({ mode, fields: FIELDS_FOR[mode].map((f) => ({ ...f })), activeIndex: null, relative: false });
    },
    stopEditing: () => set({ mode: null, fields: [], activeIndex: null, relative: false }),
    setActiveIndex: (activeIndex) => set({ activeIndex }),
    cycleField: () => set((s) => {
        if (s.mode === null) return s;
        return { activeIndex: nextFieldIndex(s.mode, s.fields, s.activeIndex) };
    }),
    /** ',' (coordinate comma): toggles the X/Y pair focus. */
    commaField: () => set((s) => {
        if (s.mode === null) return s;
        const index = s.activeIndex ?? 0;
        if (index !== 0 && index !== 1) return s;
        return { activeIndex: index === 0 ? 1 : 0 };
    }),
    typeChar: (ch) => set((s) => {
        if (s.mode === null) return s;
        const index = s.activeIndex ?? 0;
        const field = s.fields[index];
        if (!field) return s;
        // '@' as the first character flips the X/Y pair into relative entry
        // (the '#' badge) instead of typing into the field.
        if (ch === '@' && !field.typed) {
            return { relative: true, activeIndex: index };
        }
        const typed = (field.typed ?? '') + ch;
        const fields = s.fields.map((f, i) => (i === index ? { ...f, typed } : f));
        return { fields, activeIndex: index };
    }),
    backspace: () => set((s) => {
        if (s.activeIndex === null) return s;
        const field = s.fields[s.activeIndex];
        if (!field || !field.typed) return s;
        const typed = field.typed.slice(0, -1);
        return {
            fields: s.fields.map((f, i) => (i === s.activeIndex ? { ...f, typed: typed || null } : f)),
        };
    }),
    lockActive: () => set((s) => {
        if (s.activeIndex === null) return s;
        return {
            fields: s.fields.map((f, i) => (
                i === s.activeIndex && f.typed ? { ...f, locked: true } : f
            )),
        };
    }),
    setRelative: (relative) => set({ relative }),
}));

/** True when the character belongs to balloon field entry (digits/punct). */
export function isDynBoxEntryKey(key: string): boolean {
    return key.length === 1 && /^[0-9.\-@,<>]$/.test(key);
}
